import { first, orderBy } from 'lodash'
import type { Moment } from 'moment'
import moment from 'moment'

import { Collection } from 'modules/core/lib/mobx-rest'

import AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'

class AttendanceSettings extends Collection<AttendanceSetting> {
  get cacheKey(): string {
    return 'attendanceSettings'
  }

  model() {
    return AttendanceSetting
  }

  get indexes() {
    return ['attendance_employees_policy_id']
  }

  byPolicy(attendancePolicyId: number): AttendanceSetting[] {
    return this.filter({ attendance_employees_policy_id: attendancePolicyId })
  }

  orderByCreation(attendancePolicyId?: number): AttendanceSetting[] {
    if (attendancePolicyId) {
      return orderBy(
        this.byPolicy(attendancePolicyId),
        (setting: AttendanceSetting) => setting.id,
        'asc'
      )
    } else {
      return orderBy(this.models, (setting: AttendanceSetting) => setting.id, 'asc')
    }
  }

  orderByEffectiveAt(attendancePolicyId?: number | null): AttendanceSetting[] {
    if (attendancePolicyId) {
      return orderBy(
        this.byPolicy(attendancePolicyId),
        (setting: AttendanceSetting) => setting.effectiveAt,
        'asc'
      )
    } else {
      return orderBy(this.models, (setting: AttendanceSetting) => setting.effectiveAt, 'asc')
    }
  }

  isBeforeThanDate(model: AttendanceSetting, date: Moment) {
    return model.effectiveAt.isSameOrBefore(date, 'day')
  }

  isAfterThanNext(models: AttendanceSetting[], index: number, date: Moment) {
    return (
      // last one
      models[index + 1]?.effectiveAt.isAfter(date, 'day') || index === models.length - 1
    )
  }

  currentSettingWithinDate(models: AttendanceSetting[], date: Moment) {
    const currentSetting = models.find(
      (model: AttendanceSetting, index: number) =>
        this.isBeforeThanDate(model, date) && this.isAfterThanNext(models, index, date)
    )

    return currentSetting || first(models)
  }

  getSettingWithinDate(date: Moment, attendancePolicyId?: number | null) {
    return this.currentSettingWithinDate(this.orderByEffectiveAt(attendancePolicyId), date)
  }

  currentByPolicy(attendancePolicyId?: number | null) {
    return this.getSettingWithinDate(moment(), attendancePolicyId)
  }

  getVersionIndex(attendanceSetting: AttendanceSetting) {
    const settings = this.orderByCreation(attendanceSetting.policyId)

    return settings.findIndex((s) => s === attendanceSetting) + 1
  }

  policyIdWithGeolocation(): number[] {
    return this.filter({ geolocation: true }).map((setting) => setting.policyId)
  }

  async fetchEmployeeAccessSearchFieldOptions() {
    return await this.rpc('employee_access_search_field_options', {}, { method: 'GET' })
      .then((employee_access_search_field_options) => {
        return employee_access_search_field_options
      })
      .catch(() => {
        return []
      })
  }
}

const singleton: AttendanceSettings = new AttendanceSettings()

export const attendanceSettingVersionIndex = (attendanceSetting: AttendanceSetting) => {
  return singleton.getVersionIndex(attendanceSetting)
}

export const isCurrentAttendanceSetting = (attendanceSetting: AttendanceSetting) => {
  const currentSetting = singleton.currentByPolicy(attendanceSetting.policyId)

  return attendanceSetting.attributes.id === currentSetting?.attributes.id
}

export default singleton
